import { AxiosResponse } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { RoleSelector } from "@/core/components/role-selector";
import { SwitchWithLabel } from "@/core/components/switch-with-label";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { ReferralList } from "@/business-settings/common/referral-list";
import { SidebarSeparator } from "@/business-settings/common/sidebar-separator";
import { SignupSourceCodeInfo } from "@/business-settings/common/signup-source-code-info";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useDeleteUser } from "@/business-settings/users/context/delete-user-context";
import { useBusiness } from "@/core/business";
import { getUserFullName } from "@/core/utils/name";
import { SignupSource } from "@/referral-sources/referral-source";
import { useGetRepAssignedReferrals } from "@/referrals/query";
import { USER_ROLES } from "@/user/constants";
import { useGetBusinessUser, useUpsertUser } from "@/user/query";
import { useGetMyUser } from "@/user/query";
import { DetailedUser, UserRole } from "@/user/user";
import { isAdminOrOwner } from "@/user/utils";
import { WebAppUsersSidebarSkeleton } from "./web-app-user-sidebar-skeleton";

interface WebAppUserSidebarProps {
  businessId: string;
  mode: "view" | "new";
  userId: string | undefined;
  onSubmit?: (values: UserFormValues) => Promise<void>;
}

interface UserFormValues {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_notifications_enabled: boolean;
}

const validationSchema = Yup.object().shape({
  first_name: Yup.string(),
  last_name: Yup.string(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string().required("Role is required"),
  is_notifications_enabled: Yup.boolean().required(),
});

const WebAppUserSidebar = ({
  businessId,
  mode,
  userId,
}: WebAppUserSidebarProps) => {
  const { data: user, isLoading } = useGetBusinessUser(businessId, userId);

  const { isSpiffOnly } = useBusiness();
  const { updateSidebar, resetSidebar } = useBusinessSettingsSidebar();
  const { setDeleteUserId } = useDeleteUser();
  const { data: userInfo } = useGetMyUser();
  const {
    data: repAssignedReferrals,
    isLoading: isLoadingRepAssignedReferrals,
  } = useGetRepAssignedReferrals(userId || "");
  const { mutate: upsertUser } = useUpsertUser(businessId, false);
  const isNewMode = mode === "new";
  const isAdminOrOwnerUser = isAdminOrOwner(userInfo?.role);

  const initialValues = useMemo<UserFormValues>(
    () => ({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      role: user?.role || "",
      is_notifications_enabled: user?.is_notifications_enabled ?? false,
    }),
    [user],
  );

  const handleSubmit = async (
    values: UserFormValues,
    { resetForm }: FormikHelpers<UserFormValues>,
  ) =>
    upsertUser(
      {
        ...values,
        id: userId,
        role: values.role as UserRole,
      },
      {
        onSuccess: (response: AxiosResponse<DetailedUser>) => {
          const data = response.data;
          resetForm({ values });
          updateSidebar({
            mode: SIDEBAR_MODES.view,
            selectedItemId: data.id,
          });
          toast.success(
            `User ${isNewMode ? "created" : "updated"} successfully`,
          );
        },
        onError: (error) => {
          toast.error(`Failed to ${isNewMode ? "create" : "update"} user`, {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );

  const headerText = "Users";
  const name =
    user?.first_name || user?.last_name
      ? getUserFullName(user)
      : user?.email || "New User";

  const handleDeleteUserAction = () => {
    if (userId) {
      setDeleteUserId(userId);
    }
  };

  const dropdownActions =
    mode === "view" && user
      ? [
          {
            label: "Delete User",
            onClick: handleDeleteUserAction,
            variant: "destructive" as const,
          },
        ]
      : undefined;

  if (isLoading) {
    return <WebAppUsersSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <div className="flex-1 overflow-hidden">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
            <Stack
              gap="lg"
              className="flex h-full flex-col overflow-hidden"
              fullWidth
            >
              <Form className="flex-none px-6 pt-6">
                <Stack gap="lg">
                  <Stack>
                    <Stack direction="row" fullWidth>
                      <FormInput
                        label="First Name"
                        placeholder="Enter first name"
                        disabled={!isAdminOrOwnerUser}
                        disabledTooltip="Have an owner account to change the first name of this user"
                        {...getFieldProps("first_name")}
                      />
                      <FormInput
                        label="Last Name"
                        placeholder="Enter last name"
                        disabled={!isAdminOrOwnerUser}
                        disabledTooltip="Have an owner account to change the last name of this user"
                        {...getFieldProps("last_name")}
                      />
                    </Stack>

                    <FormInput
                      label="Email"
                      placeholder="Enter email address"
                      type="email"
                      required
                      fullWidth
                      disabled={!isAdminOrOwnerUser}
                      disabledTooltip="Have an owner account to change the email of this user"
                      {...getFieldProps("email")}
                    />

                    <RoleSelector
                      exclude={[USER_ROLES.TECH, USER_ROLES.ADMIN]}
                      value={values.role}
                      onChange={(value) => setFieldValue("role", value)}
                      required
                      disabled={!isAdminOrOwnerUser}
                      fullWidth
                      disabledTooltip="Have an owner account to change the role of this user"
                    />
                    <SwitchWithLabel
                      label="Enable Notifications"
                      checked={values.is_notifications_enabled}
                      onChange={(checked) =>
                        setFieldValue("is_notifications_enabled", checked)
                      }
                      disabled={!isAdminOrOwnerUser}
                      description={
                        <>
                          Email notifications{" "}
                          <span className="font-medium">
                            {values.is_notifications_enabled
                              ? "will be sent"
                              : "will not be sent"}
                          </span>{" "}
                          to this user.
                        </>
                      }
                    />
                  </Stack>

                  <Stack direction="row">
                    <Button type="submit" size="sm" disabled={!dirty}>
                      {isNewMode ? "Create User" : "Save Changes"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!dirty}
                      onClick={() => resetForm({ values: initialValues })}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  {!isNewMode && !isSpiffOnly && (
                    <SignupSourceCodeInfo signupSource={user as SignupSource} />
                  )}
                </Stack>
              </Form>
              <SidebarSeparator />
              {!isSpiffOnly && (
                <div className="min-h-0 w-full flex-1">
                  <ReferralList
                    referrals={repAssignedReferrals || []}
                    isLoading={isLoadingRepAssignedReferrals}
                  />
                </div>
              )}
            </Stack>
          )}
        </Formik>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default WebAppUserSidebar;
